<template>
	<div class="ingame-insufficient">
		<div class="ingame-insufficient__header">
			<div class="ingame-insufficient__header__title">
				{{ $t('checkout.almost-there') }}
			</div>
			<div v-if="isDisplayBalance" class="ingame-insufficient__header__balance">
				{{ $t('checkout.your-current-balance-is') }}
				<currency
					class="ingame-insufficient__header__balance__value"
					:code="totalBalanceCurrency"
					:value="totalBalance"
				/>
			</div>

			<i18n
				v-if="additionalPayment"
				class="ingame-insufficient__header__need"
				path="checkout.need-n-amount-complete"
				tag="div"
			>
				<template v-slot:amount>
					<currency
						class="ingame-insufficient__header__need__value"
						:code="additionalPaymentCurrency"
						:value="additionalPaymentAmount"
					/>
				</template>
			</i18n>

			<div class="ingame-insufficient__header__message">
				{{ $t('checkout.almost-there-details') }}
			</div>
		</div>

		<div class="ingame-insufficient__details">
			<div class="order-details">
				<div v-if="orderNumber" class="order-details__row">
					<div class="order-details__row__title">{{ $t('checkout.order-number') }}</div>
					<div class="order-details__row__value">{{ orderNumber }}</div>
				</div>
				<div v-if="orderDate" class="order-details__row">
					<div class="order-details__row__title">{{ $t('checkout.order-date') }}</div>
					<div class="order-details__row__value">{{ orderDate }}</div>
				</div>
				<div v-if="paymentMethod" class="order-details__row">
					<div class="order-details__row__title">{{ $t('checkout.payment-method') }}</div>
					<div class="order-details__row__value">{{ paymentMethod }}</div>
				</div>
				<div v-if="chargeTotalCurrency && chargeTotalAmount" class="order-details__row">
					<div class="order-details__row__title">{{ $t('checkout.charge-total') }}</div>
					<div class="order-details__row__value">
						<currency
							class="ingame-insufficient__header__balance__value"
							:code="chargeTotalCurrency"
							:value="chargeTotalAmount"
						/>
					</div>
				</div>
			</div>
		</div>

		<div class="ingame-insufficient__help">
			<div class="ingame-insufficient__help__balance">{{ $t('checkout.ingame.balance-update-moment') }}</div>
			<i18n class="ingame-insufficient__help__contacts" path="checkout.for-information-billing" tag="div">
				<template v-slot:nexonSupportCenter>
					<a :href="supportLink" target="_blank" @click="supportTracking">
						{{ $t('checkout.nexon-support-center') }}
					</a>
				</template>
			</i18n>
		</div>

		<div class="ingame-insufficient__actions">
			<div class="ingame-insufficient__actions__btn-wrapper">
				<FormButton
					class="btn-primary"
					:label="$t('cancel')"
					:button-click="close"
					:disabled="addMoreFundsLoading"
				/>
			</div>
			<div class="ingame-insufficient__actions__btn-wrapper">
				<FormButton
					:label="$t('checkout.add-more-funds')"
					:button-click="addMoreFunds"
					:loading="addMoreFundsLoading"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import { FormButton } from '@arena/toolkit';
import resultMixin from './result-mixin';
import currency from '@/components/common/currency';

export default {
	name: 'InGamePurchasePending',
	components: {
		FormButton,
		currency,
	},
	mixins: [resultMixin],
	data() {
		return {
			addMoreFundsLoading: false,
		};
	},
	computed: {
		totalBalanceCurrency() {
			return (
				this.inGameTransactionResult.account &&
				this.inGameTransactionResult.account.balance &&
				this.inGameTransactionResult.account.balance.currency
			);
		},
		totalBalance() {
			return (
				this.inGameTransactionResult.account &&
				this.inGameTransactionResult.account.balance &&
				this.inGameTransactionResult.account.balance.total
			);
		},
		isDisplayBalance() {
			return (
				this.inGameTransactionResult.account &&
				this.inGameTransactionResult.account.balance &&
				this.inGameTransactionResult.account.balance.isDisplayBalance
			);
		},
		chargeTotalAmount() {
			return this.inGameTransactionResult.payment && this.inGameTransactionResult.payment.amount;
		},
		chargeTotalCurrency() {
			return this.inGameTransactionResult.payment && this.inGameTransactionResult.payment.currency;
		},
		additionalPayment() {
			return this.inGameTransactionResult.additionalPayment;
		},
		additionalPaymentAmount() {
			return this.additionalPayment && this.additionalPayment.amount;
		},
		additionalPaymentCurrency() {
			return this.additionalPayment && this.additionalPayment.currency;
		},
		additionalPaymentToken() {
			return this.additionalPayment && this.additionalPayment.token;
		},
	},
	methods: {
		addMoreFunds() {
			this.addMoreFundsLoading = true;

			dataLayer.push({
				event: 'In Game Purchase Add More Balance',
			});

			this.$store.commit('setToken', this.additionalPaymentToken);

			this.$store.dispatch('fetchBillingInfoVerifyToken').then(() => {
				this.addMoreFundsLoading = false;
				this.$router.replace({
					name: 'in-game-purchase',
					query: {
						from: 'add-more-funds',
					},
				});
			});
		},
	},
};
</script>

<style lang="scss" scoped>
.ingame-insufficient {
	max-width: 640px;
	margin-left: auto;
	margin-right: auto;
	$balanceColor: #eda03b;
	&__header {
		text-align: center;
		line-height: 1;
		padding-bottom: 16px;
		border-bottom: 2px solid rgba(#fff, 0.5);

		&:before {
			content: '';
			display: block;
			width: 100%;
			height: 100px;
			background-image: url('~@/images/purchase-complete.png');
			background-repeat: no-repeat;
			background-size: contain;
			background-position: center center;
			margin-top: 40px;

			@media (max-width: 700px) {
				content: none;
			}
		}
		&__title {
			font-size: 24px;
			padding-top: 13px;
			color: #fff;
		}
		&__balance {
			font-size: 16px;
			line-height: 20px;
			background-color: rgba(#000, 0.5);
			border: 2px solid #8f8f8f;
			padding: 14px 20px 15px 20px;
			text-align: center;
			color: #fff;
			margin-top: 16px;
			&__value {
				color: $balanceColor;
				padding-left: 6px;
				font-weight: bold;
				@media (max-width: 600px) {
					display: block;
					padding-left: 0px;
				}
			}
		}
		&__need {
			color: #fff;
			font-size: 24px;
			padding-top: 13px;
			&__value {
				color: $balanceColor;
				font-weight: bold;
			}
		}
		&__message {
			padding-top: 10px;
			line-height: 1.3;
			max-width: 490px;
			margin: 0 auto;
		}
	}
	&__details {
		.order-details {
			&__row {
				display: flex;
				flex-wrap: wrap;
				justify-content: space-between;
				padding-top: 8px;
				line-height: 1.4;

				&:first-child {
					padding-top: 14px;
				}

				&:last-child {
					border-bottom: 1px solid rgba(#fff, 0.2);
					padding-bottom: 14px;
				}

				&__title,
				&__value {
					display: block;
					max-width: 100%;
				}
				&__title {
					margin-right: auto;
					padding-right: 15px;
				}
				&__value {
					color: #fff;
				}

				@media (max-width: 600px) {
					justify-content: start;
					&__title,
					&__value {
						flex: 0 0 100%;
						max-width: 100%;
					}
					&__value {
						word-break: break-word;
						word-wrap: break-word;
					}
				}
			}
		}
	}

	&__help {
		text-align: center;
		padding-top: 34px;
		line-height: 1.3;
		a {
			text-decoration: underline;
			text-underline-position: under;
			text-transform: capitalize;
			color: inherit;
			&:hover {
				color: #fff;
			}
		}
		@media (max-width: 700px) {
			&__balance,
			&__contacts {
				display: inline;
			}
			&__contacts {
				padding-left: 2px;
			}
		}
	}

	&__actions {
		display: flex;
		flex-wrap: wrap;
		justify-content: space-between;
		margin-top: 16px;
		&__btn-wrapper {
			flex: 0 0 50%;
			max-width: 50%;
			padding: 0 10px;

			@media (max-width: 700px) {
				flex: 0 0 100%;
				max-width: 100%;
				padding: 0px;
			}

			button {
				margin-top: 20px;
				&.disabled {
					&:active {
						color: #262626;
						background-color: #fff;
						border-color: #d9d9d9;
					}
				}
				&.disabled.btn-primary {
					&:active,
					&:hover {
						color: #fff;
						background-color: #4d4d4d;
						border-color: #4d4d4d;
					}
				}
			}
			&:first-child {
				padding-left: 0px;
			}
			&:last-child {
				padding-right: 0px;
			}
		}
	}
}
</style>
